/* global axios */
import ApiClient from '../ApiClient';

const positiveInteger = (
  value,
  name,
  { max = Number.MAX_SAFE_INTEGER } = {}
) => {
  const normalizedValue = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(normalizedValue)) {
    throw new TypeError(`${name} must be a positive integer`);
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isSafeInteger(numericValue) || numericValue > max) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return normalizedValue;
};

const dashboardDate = (value, name) => {
  const normalizedValue = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    throw new TypeError(`${name} must use YYYY-MM-DD`);
  }
  return normalizedValue;
};

class AnalyticsAPI extends ApiClient {
  constructor() {
    super('pipeline/analytics', { accountScoped: true });
  }

  getWinRate({ startDate, endDate, pipelineId } = {}) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (pipelineId) params.pipeline_id = pipelineId;

    return axios.get(`${this.url}/win_rate`, { params });
  }

  getConversionMetrics({ startDate, endDate, pipelineId }) {
    const params = { pipeline_id: pipelineId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return axios.get(`${this.url}/conversion_metrics`, { params });
  }

  getSalesVelocity({ startDate, endDate, pipelineId } = {}) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (pipelineId) params.pipeline_id = pipelineId;

    return axios.get(`${this.url}/sales_velocity`, { params });
  }

  // Auditoria 2026-08-08 (G4): o `ForecastService` existia inteiro no backend,
  // com endpoint roteado e specs, e nenhum lugar do dashboard o chamava.
  getForecast({ monthsAhead, pipelineId } = {}) {
    const params = {};
    if (monthsAhead) params.months_ahead = monthsAhead;
    if (pipelineId) params.pipeline_id = pipelineId;

    return axios.get(`${this.url}/forecast`, { params });
  }

  getDashboard({ startDate, endDate } = {}) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return axios.get(`${this.url}/dashboard`, { params });
  }

  getPipelineDashboard({
    accountId,
    pipelineId,
    dateStart = null,
    dateEnd = null,
    activityPage = 1,
    activityPerPage = 10,
  } = {}) {
    const normalizedAccountId = positiveInteger(accountId, 'accountId');
    const params = {
      pipeline_id: positiveInteger(pipelineId, 'pipelineId'),
      activity_page: Number(positiveInteger(activityPage, 'activityPage')),
      activity_per_page: Number(
        positiveInteger(activityPerPage, 'activityPerPage', { max: 50 })
      ),
    };
    const hasDateStart = dateStart !== null && dateStart !== undefined;
    const hasDateEnd = dateEnd !== null && dateEnd !== undefined;
    if (hasDateStart !== hasDateEnd) {
      throw new TypeError('dateStart and dateEnd must be provided together');
    }
    if (hasDateStart) {
      params.date_start = dashboardDate(dateStart, 'dateStart');
      params.date_end = dashboardDate(dateEnd, 'dateEnd');
    }

    return axios.get(
      `${this.apiVersion}/accounts/${encodeURIComponent(
        normalizedAccountId
      )}/pipeline/analytics/pipeline_dashboard`,
      { params }
    );
  }

  // achado 07 (metade do frontend) — exportação CSV do relatório. Mesmo
  // contrato de parâmetros do dashboard (pipeline_id opcional + intervalo de
  // datas), reaproveitando o padrão accountScoped via `this.url` que o resto
  // deste arquivo já usa (ao contrário de `getPipelineDashboard`, que monta a
  // URL com um accountId explícito). `responseType: 'blob'` + `config`
  // repassado espelham `AppointmentsAPI.exportCsv`, que já sustenta o
  // AbortController usado no cliente para cancelar um download em voo quando
  // a conta troca no meio.
  exportCsv({ pipelineId, startDate, endDate } = {}, config = {}) {
    const params = {};
    if (pipelineId) params.pipeline_id = pipelineId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return axios.get(`${this.url}/export`, {
      ...config,
      params,
      responseType: 'blob',
    });
  }

  getPipelineAnalysis(pipelineId, startDate, endDate) {
    const params = {
      pipeline_id: pipelineId,
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return axios.get(`${this.url}/pipeline_analysis`, { params });
  }
}

export default new AnalyticsAPI();
